import { useMemo } from 'react'
import { Table, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { CameraOutlined } from '@ant-design/icons'
import { PriceCheckerActions } from './DeviceActions'
import { isCheckerProblematic, type PriceChecker } from './utils'
import styles from '@/pages/HomePage.module.css'

interface Row extends PriceChecker {
  key: string
  title: string
  statusLabel?: string
  ping: string | null
  isOffline: boolean
  isOnline: boolean
}

interface Props {
  checkers: PriceChecker[]
  checkerStatus: Record<string, string>
  problemsOnly: boolean
  onSelectRow: (checker: PriceChecker) => void
}

function copyIp(ip?: string) {
  if (!ip) return
  void navigator.clipboard?.writeText(ip)
}

export function PriceCheckerTable({
  checkers,
  checkerStatus,
  problemsOnly,
  onSelectRow,
}: Props) {
  const rows = useMemo(() => {
    const all: Row[] = checkers.map((c) => {
      const statusLabel = checkerStatus[String(c.id)]
      const isOffline = statusLabel ? /оффлайн/i.test(statusLabel) : c.online === false
      const isOnline = statusLabel ? /онлайн/i.test(statusLabel) : c.online === true
      return {
        ...c,
        key: String(c.id),
        title: c.name || `Прайс-чекер ${c.id}`,
        statusLabel,
        ping: statusLabel?.match(/\d+(\.\d+)?ms/i)?.[0] ?? null,
        isOffline,
        isOnline,
      }
    })
    const filtered = problemsOnly
      ? all.filter((r) => isCheckerProblematic(r.statusLabel, r))
      : all
    return filtered.sort((a, b) => Number(a.isOffline) - Number(b.isOffline))
  }, [checkers, checkerStatus, problemsOnly])

  const columns: ColumnsType<Row> = [
    {
      title: 'Статус',
      width: 130,
      render: (_, row) => {
        const text = row.isOffline ? 'Оффлайн' : row.isOnline ? 'Онлайн' : 'Проверка…'
        return (
          <span
            className={[
              styles.statusCell,
              row.isOnline ? styles.statusOn : '',
              row.isOffline ? styles.statusOff : '',
              !row.isOnline && !row.isOffline ? styles.statusMuted : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <i className={styles.statusDot} aria-hidden />
            <span>
              {text}
              {row.ping ? <span className={styles.statusPing}> · {row.ping}</span> : null}
            </span>
          </span>
        )
      },
    },
    {
      title: 'Устройство',
      render: (_, row) => (
        <div className={styles.deviceCell}>
          <span className={styles.deviceCellIcon}>
            <CameraOutlined />
          </span>
          <div className={styles.deviceCellText}>
            <div className={styles.deviceName}>{row.title}</div>
            <div className={styles.deviceType}>Прайс-чекер</div>
          </div>
        </div>
      ),
    },
    {
      title: 'IP',
      width: 140,
      render: (_, row) => (
        <Tooltip title="Скопировать">
          <button
            type="button"
            className={styles.ipBtn}
            onClick={(e) => {
              e.stopPropagation()
              copyIp(row.ip)
            }}
          >
            {row.ip || '—'}
          </button>
        </Tooltip>
      ),
    },
    {
      title: 'Действия',
      width: 160,
      render: (_, row) => (
        <div onClick={(e) => e.stopPropagation()} role="presentation">
          <PriceCheckerActions checkerId={row.id} />
        </div>
      ),
    },
  ]

  return (
    <Table<Row>
      className={styles.dataTable}
      columns={columns}
      dataSource={rows}
      pagination={false}
      tableLayout="fixed"
      size="small"
      rowClassName={(row) => (row.isOffline ? styles.rowOffline : '')}
      onRow={(row) => ({
        onClick: () => onSelectRow(row),
        style: { cursor: 'pointer' },
      })}
    />
  )
}
